import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Card from '../common/Card';
import Button from '../common/Button';
import Input from '../common/Input';
import api from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import '../../styles/low-stock.css';

const StockAdjust = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const initial = location.state?.product || null;
  const preferredAction = location.state?.action || 'receive';

  const [product, setProduct] = useState(initial);
  const [suppliers, setSuppliers] = useState([]);
  const [action, setAction] = useState(preferredAction);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    quantity: '',
    supplier_id: '',
    unit_price: '',
    reason: 'Restock',
    notes: ''
  });

  useEffect(() => {
    loadProduct();
    loadSuppliers();
  }, [id]);

  const loadProduct = async () => {
    if (initial && String(initial.id) === String(id)) {
      setProduct(initial);
      setForm((f) => ({ ...f, unit_price: initial.cost_price || '' }));
      return;
    }
    try {
      const res = await api.get(`/products.php/${id}`);
      const p = res.data.product || res.data;
      setProduct(p);
      setForm((f) => ({ ...f, unit_price: p.cost_price || '' }));
    } catch (err) {
      toast.error('Failed to load product');
    }
  };

  const loadSuppliers = async () => {
    try {
      const res = await api.get('/suppliers.php');
      setSuppliers((res.data.suppliers || []).filter((s) => Number(s.is_active) !== 0));
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const qty = parseFloat(form.quantity);
    if (!qty || qty === 0) {
      toast.error('Enter a valid quantity');
      return;
    }

    setSaving(true);
    try {
      if (action === 'receive') {
        await api.post('/inventory.php/receive', {
          product_id: Number(id),
          quantity: Math.abs(qty),
          supplier_id: form.supplier_id ? Number(form.supplier_id) : null,
          unit_price: form.unit_price ? parseFloat(form.unit_price) : undefined,
          notes: form.notes || form.reason
        });
        toast.success('Stock received');
      } else {
        await api.post('/inventory.php/adjust', {
          product_id: Number(id),
          quantity: qty,
          reason: form.reason || 'Adjustment',
          notes: form.notes
        });
        toast.success('Stock adjusted');
      }
      navigate('/products/low-stock');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Stock update failed');
    } finally {
      setSaving(false);
    }
  };

  if (!product) {
    return <div className="loading">Loading product…</div>;
  }

  return (
    <div className="stock-adjust-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
        <div>
          <h1>Stock Update</h1>
          <p>{product.product_name} · Current: {product.current_stock} {product.unit || 'units'}</p>
        </div>
        <Button variant="secondary" onClick={() => navigate(-1)}>Back</Button>
      </div>

      <Card>
        <div className="stock-action-tabs">
          <button
            type="button"
            className={action === 'receive' ? 'active' : ''}
            onClick={() => setAction('receive')}
          >
            Receive stock
          </button>
          <button
            type="button"
            className={action === 'adjust' ? 'active' : ''}
            onClick={() => setAction('adjust')}
          >
            Adjust stock
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <Input
            label={action === 'receive' ? 'Quantity to receive' : 'Adjustment (+/-)'}
            name="quantity"
            type="number"
            value={form.quantity}
            onChange={(e) => setForm({ ...form, quantity: e.target.value })}
            required
            hint={action === 'adjust' ? 'Use negative numbers to reduce stock' : ''}
          />

          {action === 'receive' && (
            <>
              <Input
                type="select"
                label="Supplier (optional)"
                name="supplier_id"
                value={form.supplier_id}
                onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}
              >
                <option value="">No supplier</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.supplier_name}</option>
                ))}
              </Input>
              <Input
                label="Unit cost"
                name="unit_price"
                type="number"
                value={form.unit_price}
                onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
              />
            </>
          )}

          {action === 'adjust' && (
            <Input
              label="Reason"
              name="reason"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              required
            />
          )}

          <Input
            type="textarea"
            label="Notes"
            name="notes"
            rows={3}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />

          {product.cost_price != null && (
            <p className="hint">Cost price on file: {formatCurrency(product.cost_price)}</p>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <Button type="submit" variant="primary" loading={saving}>
              Save
            </Button>
            <Button type="button" variant="secondary" onClick={() => navigate('/products')}>
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default StockAdjust;
